import type { GameAction, GameState, Player } from './types';
import { PAIRS_PER_DECK } from './types';
import {
  buildDeck,
  buildPlayers,
  checkWinner,
  emptyPairs,
  nextActiveIndex,
} from './rules';

export const initialState: GameState = {
  phase: 'splash',
  modalType: null,

  playerCount: 2,
  playerNames: ['農夫 1', '農夫 2', '農夫 3', '農夫 4', '農夫 5'],
  cardPairs: emptyPairs(),

  players: [],
  cards: [],
  turn: 0,
  flippedIndices: [],
  matchedCount: 0,
  isProcessing: false,
  lastVictimId: null,
  showTurnLayer: false,
  winner: null,

  savedDecks: {},
  customApiKey: '',

  soundEnabled: true,
  bgmEnabled: false,
  ttsEnabled: true,

  showRules: false,
  setupError: '',
  notification: '',
  aiTopic: '',
  aiError: '',
  aiStatusMessage: '',
  isGeneratingPairs: false,
  aiCommentary: '',
  isGeneratingCommentary: false,

  saveModalOpen: false,
  saveName: '',
  shareModalOpen: false,
  shareLink: '',
  keyModalOpen: false,
  confirmModal: { open: false, message: '', onConfirm: null },
};

function applyFruitChange(players: Player[], id: number, delta: number): Player[] {
  return players.map((p) => {
    if (p.id !== id) return p;
    const fruits = Math.max(0, p.fruits + delta);
    return { ...p, fruits, isEliminated: fruits <= 0 };
  });
}

export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'SPLASH_DONE':
      return { ...state, phase: 'intro' };

    case 'GO_TO_SETUP':
      return { ...state, phase: 'setup' };

    case 'GO_TO_INTRO':
      return {
        ...state,
        phase: 'intro',
        modalType: null,
        winner: null,
        aiCommentary: '',
        showTurnLayer: false,
        confirmModal: { open: false, message: '', onConfirm: null },
      };

    case 'SHOW_RULES':
      return { ...state, showRules: action.show };

    case 'SET_PLAYER_COUNT':
      return { ...state, playerCount: action.count };

    case 'SET_PLAYER_NAME': {
      const names = [...state.playerNames];
      names[action.index] = action.name;
      return { ...state, playerNames: names };
    }

    case 'SET_CARD_PAIRS':
      return { ...state, cardPairs: action.pairs, setupError: '' };

    case 'UPDATE_CARD_PAIR': {
      const pairs = [...state.cardPairs];
      const cur = pairs[action.index] ?? { q: '', a: '' };
      pairs[action.index] = { ...cur, [action.field]: action.value };
      return { ...state, cardPairs: pairs, setupError: '' };
    }

    case 'CLEAR_SETUP_ERROR':
      return { ...state, setupError: '' };

    case 'SET_SETUP_ERROR':
      return { ...state, setupError: action.message };

    case 'START_GAME': {
      const players = buildPlayers(state.playerCount, state.playerNames);
      const cards = buildDeck(state.cardPairs);
      return {
        ...state,
        phase: 'playing',
        modalType: null,
        players,
        cards,
        turn: 0,
        flippedIndices: [],
        matchedCount: 0,
        isProcessing: false,
        lastVictimId: null,
        showTurnLayer: true,
        winner: null,
        aiCommentary: '',
      };
    }

    case 'RESHUFFLE':
      return {
        ...state,
        cards: buildDeck(state.cardPairs),
        matchedCount: 0,
        flippedIndices: [],
        isProcessing: false,
      };

    case 'FLIP_CARD': {
      if (state.isProcessing || state.modalType !== null) return state;
      const card = state.cards[action.index];
      if (!card || card.isFlipped || card.isMatched) return state;
      const nextCards = state.cards.map((c, i) =>
        i === action.index ? { ...c, isFlipped: true } : c,
      );
      const flipped = [...state.flippedIndices, action.index];
      return {
        ...state,
        cards: nextCards,
        flippedIndices: flipped,
        isProcessing: flipped.length === 2,
      };
    }

    case 'RESOLVE_MATCH': {
      const [i1, i2] = state.flippedIndices;
      if (i1 === undefined || i2 === undefined) return state;
      const cards = state.cards.map((c, i) =>
        i === i1 || i === i2 ? { ...c, isMatched: true } : c,
      );
      return {
        ...state,
        cards,
        flippedIndices: [],
        isProcessing: false,
        matchedCount: state.matchedCount + 1,
        modalType: 'action',
      };
    }

    case 'RESOLVE_MISMATCH': {
      const [i1, i2] = state.flippedIndices;
      if (i1 === undefined || i2 === undefined) return state;
      const cards = state.cards.map((c, i) =>
        i === i1 || i === i2 ? { ...c, isFlipped: false } : c,
      );
      return {
        ...state,
        cards,
        flippedIndices: [],
        isProcessing: false,
        turn: nextActiveIndex(state.players, state.turn),
        showTurnLayer: true,
      };
    }

    case 'OPEN_STEAL_MODAL':
      return { ...state, modalType: 'steal' };

    case 'CLOSE_STEAL_MODAL':
      return { ...state, modalType: 'action' };

    case 'HARVEST': {
      const players = applyFruitChange(state.players, state.turn, 2);
      return { ...state, players, modalType: 'decision' };
    }

    case 'STEAL': {
      let players = applyFruitChange(state.players, action.targetId, -1);
      players = applyFruitChange(players, state.turn, 1);
      return {
        ...state,
        players,
        lastVictimId: action.targetId,
        modalType: 'decision',
      };
    }

    case 'CONTINUE_TURN':
      return { ...state, modalType: null };

    case 'END_TURN':
      return {
        ...state,
        modalType: null,
        turn: nextActiveIndex(state.players, state.turn),
        showTurnLayer: true,
      };

    case 'CHECK_WIN': {
      const w = checkWinner(state.players);
      if (!w || state.phase !== 'playing') return state;
      return {
        ...state,
        phase: 'finished',
        modalType: 'winner',
        winner: w,
      };
    }

    case 'SHOW_TURN_LAYER':
      return { ...state, showTurnLayer: true };

    case 'HIDE_TURN_LAYER':
      return { ...state, showTurnLayer: false };

    case 'LOAD_PERSISTED':
      return {
        ...state,
        savedDecks: action.decks,
        customApiKey: action.apiKey,
      };

    case 'LOAD_SHARED_DECK':
      return {
        ...state,
        cardPairs: action.pairs,
        phase: 'setup',
        notification: '🎉 成功載入分享的題庫！',
      };

    case 'SAVE_DECK': {
      const next = { ...state.savedDecks, [action.name]: state.cardPairs };
      return { ...state, savedDecks: next, saveModalOpen: false };
    }

    case 'DELETE_DECK': {
      const next = Object.fromEntries(
        Object.entries(state.savedDecks).filter(([k]) => k !== action.name),
      );
      return { ...state, savedDecks: next, confirmModal: { open: false, message: '', onConfirm: null } };
    }

    case 'SET_API_KEY':
      return { ...state, customApiKey: action.key };

    case 'CLEAR_API_KEY':
      return { ...state, customApiKey: '', keyModalOpen: false, notification: '🗑️ 金鑰已清除！' };

    case 'TOGGLE_SOUND':
      return { ...state, soundEnabled: !state.soundEnabled };
    case 'TOGGLE_BGM':
      return { ...state, bgmEnabled: !state.bgmEnabled };
    case 'TOGGLE_TTS':
      return { ...state, ttsEnabled: !state.ttsEnabled };

    case 'SET_NOTIFICATION':
      return { ...state, notification: action.message };
    case 'CLEAR_NOTIFICATION':
      return { ...state, notification: '' };

    case 'OPEN_SAVE_MODAL':
      return { ...state, saveModalOpen: true, saveName: action.defaultName };
    case 'CLOSE_SAVE_MODAL':
      return { ...state, saveModalOpen: false };
    case 'SET_SAVE_NAME':
      return { ...state, saveName: action.name };

    case 'OPEN_SHARE_MODAL':
      return { ...state, shareModalOpen: true, shareLink: action.link };
    case 'CLOSE_SHARE_MODAL':
      return { ...state, shareModalOpen: false };

    case 'OPEN_KEY_MODAL':
      return { ...state, keyModalOpen: true };
    case 'CLOSE_KEY_MODAL':
      return { ...state, keyModalOpen: false };

    case 'OPEN_CONFIRM':
      return {
        ...state,
        confirmModal: { open: true, message: action.message, onConfirm: action.onConfirm },
      };
    case 'CLOSE_CONFIRM':
      return { ...state, confirmModal: { open: false, message: '', onConfirm: null } };

    case 'SET_AI_TOPIC':
      return { ...state, aiTopic: action.topic };
    case 'AI_GEN_START':
      return {
        ...state,
        isGeneratingPairs: true,
        aiError: '',
        aiStatusMessage: 'AI 思考中...',
      };
    case 'AI_GEN_SUCCESS':
      return {
        ...state,
        cardPairs: action.pairs,
        isGeneratingPairs: false,
        aiStatusMessage: '',
        aiError: '',
      };
    case 'AI_GEN_FALLBACK':
      return {
        ...state,
        cardPairs: action.pairs,
        isGeneratingPairs: false,
        aiStatusMessage: action.message,
      };
    case 'AI_GEN_ERROR':
      return { ...state, isGeneratingPairs: false, aiError: action.message };
    case 'CLEAR_AI_STATUS':
      return { ...state, aiStatusMessage: '', aiError: '' };

    case 'COMMENTARY_START':
      return { ...state, isGeneratingCommentary: true };
    case 'COMMENTARY_SUCCESS':
      return { ...state, isGeneratingCommentary: false, aiCommentary: action.text };
    case 'COMMENTARY_ERROR':
      return { ...state, isGeneratingCommentary: false, aiCommentary: action.text };

    default: {
      const _exhaustive: never = action;
      void _exhaustive;
      return state;
    }
  }
}

// 注意：在 PAIRS_PER_DECK 與 reducer 之間維持 invariant — cardPairs.length === PAIRS_PER_DECK
// （由 emptyPairs / normalizePairs / decodeDeckFromUrl 共同保證）
void PAIRS_PER_DECK;
